import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FaPlus, FaTrash, FaSpinner, FaCopy } from "react-icons/fa";
import PageHeading from "../PageHeading";
import { useTranslation } from "react-i18next";
import DynamicSelect from "../DynamicSelect";
import { ToastContainer, toast } from "react-toastify";

// confiq & constent
const API = "https://api.muktihospital.com/api/home";
const DEFAULT_FEATURE = { subtitle: "", title: "", icon: "" };
const DEFAULT_SERVICE = { serviceTitle: "", serviceIcon: "" };
const DEFAULT_WHY_CHOOSE_SERVICE = { serviceTitle: "", serviceDescription: "", serviceIcon: "" };
const DEFAULT_PROCESS = { icon: "", title: "" };

// reusable components
const FormInput = ({ label, id, value, onChange, placeholder }) => (
  <div className="mb-4">
    <label className="label" htmlFor={id}>{label}</label>
    <input 
      className="input-field"
      type="text" id={id} placeholder={placeholder || `Enter ${label.toLowerCase()}`}
      value={value || ""} onChange={onChange}
    />
  </div>
);

const FormTextarea = ({ label, id, value, onChange }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor={id}>{label}</label>
    <textarea
      className="w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500"
      id={id} rows={5} placeholder={`Enter ${label.toLowerCase()}`}
      value={value || ""} onChange={onChange}
    />
  </div>
);

const IconUpload = ({ id, onChange, filename }) => {
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  return(
   <div className="mt-4">
   <p className="text-[10px] text-gray-600 my-1">
     Max file size: {MAX_FILE_SIZE / 1024 / 1024}MB. Allowed types: JPEG, PNG, GIF, WEBP
   </p>
   <input
     type="file"
     accept="image/*"
     className="border p-2 rounded w-full"
     id={id}
     onChange={onChange}
   />
    {filename && <span className="text-sm text-gray-500">{filename}</span>}
 </div>
  )
}

const ImageUpload = ({ id, label, file, setFile, currentImage,  imgClassName = "w-full max-w-96" }) => {
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  return (
    <div className="mb-6">
    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor={id}>{label}</label>
    {file ? (
      <div className="mt-4 flex justify-left">
        <img src={URL.createObjectURL(file)} alt={`${label} Preview`} className={`${imgClassName} object-cover rounded-lg shadow-xl border-2 border-gray-200`} />
      </div>
    ) : currentImage && (
      <div className="mt-4">
        <img src={getImageUrl(currentImage)} alt={`Current ${label}`} className={`${imgClassName} object-cover rounded-lg shadow-xl border-2 border-gray-200`} />
      </div>
    )}
    <div className="mt-4">
        <p className="text-[10px] text-gray-600 mt-1">
          Max file size: {MAX_FILE_SIZE / 1024 / 1024}MB. Allowed types: JPEG, PNG, GIF, WEBP
        </p>
        <input
          type="file"
          accept="image/*"
          className="border p-2 rounded w-full"
          id={id}
          onChange={(e) => setFile(e.target.files[0])} 
        />
      </div>
  </div>
  )
}

const IconPreview = ({file, icon}) => {
  if (!file && (!icon || icon === "icon-selected")) return null;
  
  return (
    <div className="mt-2 flex items-center space-x-2">
      <div className="w-12 h-12 border rounded-md flex items-center justify-center overflow-hidden bg-white">
        {file ? (
          <img src={URL.createObjectURL(file)} alt="Icon Preview" className="max-w-full max-h-full object-contain" />
        ) : icon && icon !== "icon-selected" ? (
          <img src={getImageUrl(icon)} alt="Icon Preview" className="max-w-full max-h-full object-contain" />
        ) : null}
      </div>
    </div>

    
  );
};

// helper function
const getImageUrl = (image) => {
  if (!image) return "";
  if (typeof image === "string") {
    if (image === "icon-selected") return "";
    return image.startsWith("http") ? image : `https://api.muktihospital.com/${image}`;
  }
  return URL.createObjectURL(image);
};

const ensureArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  
  // check array from object
  if (typeof data === 'object') {
    const keys = Object.keys(data);
    if (keys.length > 0 && keys.every(key => !isNaN(parseInt(key)))) {
      return Object.values(data);
    }
    
    // nested property check
    for (const key in data) {
      if (Array.isArray(data[key])) {
        return data[key];
      }
    }
  }
  
  return [data]; 
};

const parseData = (data) => {
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch (error) {
      console.error("JSON parsing error:", error);
      return data;
    }
  }
  return data;
};

// main componnet
const HomepageForm = ({isSearchable}) => {
  // lamguage & loading state
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState("en");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [homepageExists, setHomepageExists] = useState(false);

  // section data state
  const [heroData, setHeroData] = useState({ prefix: "", title: "" });
  const [featuresList, setFeaturesList] = useState([DEFAULT_FEATURE]);
  const [aboutData, setAboutData] = useState({ 
    title: "", subtitle: "", description: "", experience: "", services: [] 
  });
  const [whyChooseData, setWhyChooseData] = useState({ 
    title: "", subtitle: "", description: "", 
    services: [DEFAULT_WHY_CHOOSE_SERVICE] 
  });
  const [downloadAppData, setDownloadAppData] = useState({ title: "", subtitle: "", description: "" });
  const [appointmentProcessData, setAppointmentProcessData] = useState([DEFAULT_PROCESS]); 
  const [appointmentData, setAppointmentData] = useState({});
  const [seoData, setSeoData] = useState({ metaTitle: "", metaDescription: "" });

  //file state
  const [heroFile, setHeroFile] = useState(null);
  const [aboutFiles, setAboutFiles] = useState([]);
  const [appointmentFile, setAppointmentFile] = useState(null);
  const [downloadAppFile, setDownloadAppFile] = useState(null);
  const [whyChooseFile, setWhyChooseFile] = useState(null);
  
  //icon file state
  const [featureIconFiles, setFeatureIconFiles] = useState([]);
  const [serviceIconFiles, setServiceIconFiles] = useState([]);
  const [whyChooseServiceIconFiles, setWhyChooseServiceIconFiles] = useState([]);
  const [appointmentProcessIconFiles, setAppointmentProcessIconFiles] = useState([]);

  const showSuccessMessage = (message) => {
  toast.success(message, { autoClose: 3000 });
  };
  const showErrorMessage = (message) => {
    toast.error(message, { autoClose: 5000 });
  };

  // generic item handeler
  const handleItemChange = (items, setItems, index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  // handelier function create
  const createItemHandler = (items, setItems) => 
    (index, field, value) => handleItemChange(items, setItems, index, field, value);

  // icon upload handelier
  const handleIconUpload = (files, setFiles, items, setItems, index, file, iconField = "icon") => {
    if (!file) return;
    
    const updatedIconFiles = [...files];
    updatedIconFiles[index] = file;
    setFiles(updatedIconFiles);
    
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [iconField]: "icon-selected" };
    setItems(updatedItems);
  };

  // General Handelar function
  const handleFeatureChange = createItemHandler(featuresList, setFeaturesList);
  const handleServiceChange = (index, field, value) => {
    const services = [...(aboutData.services || [])];
    while (services.length <= index) services.push(DEFAULT_SERVICE);
    handleItemChange(services, (newServices) => setAboutData({...aboutData, services: newServices}), index, field, value);
  };
  const handleWhyChooseChange = createItemHandler(whyChooseData.services, 
    (newServices) => setWhyChooseData({...whyChooseData, services: newServices}));
  const handleAppointmentProcessChange = createItemHandler(appointmentProcessData, setAppointmentProcessData);

  //icon upload handelar
  const handleFeatureIconUpload = (index, file) => 
    handleIconUpload(featureIconFiles, setFeatureIconFiles, featuresList, setFeaturesList, index, file);
  
  const handleServiceIconUpload = (index, file) => {
    if (!file) return;
    const services = [...(aboutData.services || [])];
    while (services.length <= index) services.push(DEFAULT_SERVICE);
    handleIconUpload(serviceIconFiles, setServiceIconFiles, services, 
      (newServices) => setAboutData({...aboutData, services: newServices}), index, file, "serviceIcon");
  };
  
  const handleWhyChooseServiceIconUpload = (index, file) => 
    handleIconUpload(whyChooseServiceIconFiles, setWhyChooseServiceIconFiles, 
      whyChooseData.services, (newServices) => setWhyChooseData({...whyChooseData, services: newServices}), 
      index, file, "serviceIcon");
  
  const handleAppointmentProcessIconUpload = (index, file) => 
    handleIconUpload(appointmentProcessIconFiles, setAppointmentProcessIconFiles, 
      appointmentProcessData, setAppointmentProcessData, index, file);

  // item add/remove function
  const addItem = (items, setItems, itemTemplate, files, setFiles) => {
    setItems([...items, itemTemplate]);
    setFiles([...files, null]);
  };
  
  const removeItem = (items, setItems, index, files, setFiles) => {
    setItems(items.filter((_, i) => i !== index));
    setFiles([...files].filter((_, i) => i !== index));
  };

  //specific item add/remove function
  const addFeature = () => addItem(featuresList, setFeaturesList, DEFAULT_FEATURE, featureIconFiles, setFeatureIconFiles);
  const removeFeature = (index) => removeItem(featuresList, setFeaturesList, index, featureIconFiles, setFeatureIconFiles);
  
  const addService = () => {
    const services = aboutData.services || [];
    setAboutData({...aboutData, services: [...services, DEFAULT_SERVICE]});
    setServiceIconFiles([...serviceIconFiles, null]);
  };
  
  const removeService = (index) => {
    const services = aboutData.services || [];
    setAboutData({...aboutData, services: services.filter((_, i) => i !== index)});
    setServiceIconFiles([...serviceIconFiles].filter((_, i) => i !== index));
  };
  
  const addChooseService = () => addItem(whyChooseData.services, 
    (newServices) => setWhyChooseData({...whyChooseData, services: newServices}), 
    DEFAULT_WHY_CHOOSE_SERVICE, whyChooseServiceIconFiles, setWhyChooseServiceIconFiles);
  
  const removeChooseService = (index) => removeItem(whyChooseData.services, 
    (newServices) => setWhyChooseData({...whyChooseData, services: newServices}), 
    index, whyChooseServiceIconFiles, setWhyChooseServiceIconFiles);
  
  const addAppointmentProcess = () => addItem(appointmentProcessData, setAppointmentProcessData, 
    DEFAULT_PROCESS, appointmentProcessIconFiles, setAppointmentProcessIconFiles);
  
  const removeAppointmentProcess = (index) => removeItem(appointmentProcessData, 
    setAppointmentProcessData, index, appointmentProcessIconFiles, setAppointmentProcessIconFiles);

    // const handleFileChange = (e, setState, maxFiles = 4) => {
    //   const files = Array.from(e.target.files);
      
    //   if (files.length > 0) {
    //     if (files.length <= maxFiles) {
    //       setState(files);
    //       console.log(`Selected ${files.length} files:`, files.map(f => f.name));
    //     } else {
    //       alert(`You can only select up to ${maxFiles} files.`);
    //     }
    //   }
    // };

const handleFileChange = (e, setState, maxFiles = 4) => {
  const newFiles = Array.from(e.target.files).filter(
    (file) => file.size <= 5 * 1024 * 1024
  );
  if (newFiles.length !== e.target.files.length) {
    alert("Some files exceed the 5MB size limit and were ignored.");
  }
  if (newFiles.length > 0) {
    setState((prevFiles) => {
      const updatedFiles = [...prevFiles, ...newFiles];
      if (updatedFiles.length <= maxFiles) {
        // console.log(`Selected ${updatedFiles.length} files:`, updatedFiles.map(f => f.name));
        return updatedFiles;
      } else {
        alert(`You can only select up to ${maxFiles} files.`);
        return prevFiles;
      }
    });
  }
};

  // section data process handelar
  const processSection = useCallback((section, setter, transformer = (data) => data) => {
    if (section?.translations && section.translations[language]) {
      // speciofic language data
      const data = parseData(section.translations[language]);
      setter(transformer(data));
    } else if (section?.translations?.en) {
      //speciofic language data not found
      // console.log(`No data found for language: ${language}, showing English data instead`);
      const data = parseData(section.translations.en);
      setter(transformer(data));
    } else {
      // no data found for any language
      // console.log(`No data found for any language in section`);
      setter(transformer({}));
    }
  }, [language]);


  // homepage data fetch handelar
  const fetchHomepage = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // langugae parameter add
      const res = await axios.get(`${API}?language=${language}`);
      // console.log(res.data);
      
      if (res.data) {
        setHomepageExists(true);
        const {
          heroSection, featuresSection, aboutSection, appointmentSection,
          whyChooseUsSection, downloadAppSection, appointmentProcess,
          seo,
        } = res.data;

        const seoForLang = (seo && (seo[language] || seo.en)) || {};
        setSeoData({
          metaTitle: seoForLang.metaTitle || "",
          metaDescription: seoForLang.metaDescription || "",
        });
        
        // hero section
        processSection(heroSection, setHeroData);
        
        // features section
        processSection(featuresSection, (data) => {
          const featuresArray = ensureArray(data);
          setFeaturesList(featuresArray.length > 0 ? featuresArray : [DEFAULT_FEATURE]);
          setFeatureIconFiles(new Array(featuresArray.length).fill(null));
        });
        
        // About section
        processSection(aboutSection, (data) => {
          if (data.services) {
            data.services = ensureArray(data.services);
            setServiceIconFiles(new Array(data.services.length).fill(null));
          } else {
            data.services = [];
            setServiceIconFiles([]);
          }
          setAboutData(data);
        });
        
        // Appointment section
        processSection(appointmentSection, setAppointmentData);
        
        // WHY CHOOSE US section
        processSection(whyChooseUsSection, (data) => {
          if (data.services) {
            data.services = ensureArray(data.services);
            setWhyChooseServiceIconFiles(new Array(data.services.length).fill(null));
          } else {
            data.services = [DEFAULT_WHY_CHOOSE_SERVICE];
            setWhyChooseServiceIconFiles([null]);
          }
          setWhyChooseData(data);
        });
        
        // Download App section
        processSection(downloadAppSection, setDownloadAppData);
        
        // Appointment Process
        processSection(appointmentProcess, (data) => {
          const processArray = ensureArray(data);
          setAppointmentProcessData(processArray.length > 0 ? processArray : [DEFAULT_PROCESS]);
          setAppointmentProcessIconFiles(new Array(processArray.length || 1).fill(null));
        });
      } else {
        setHomepageExists(false);
      }
    } catch (error) {
      console.error("Error fetching homepage:", error);
      showErrorMessage("Error loading homepage data: " + (error.response?.data?.error || error.message));
    } finally {
      setIsLoading(false);
    }
  }, [language, processSection]);

  // change language handelar
  useEffect(() => {
    fetchHomepage();
  }, [language, fetchHomepage]);

  // Homepage create handelar
  const handleCreateHomepage = async () => {
    try {
      const formData = new FormData();
      
      // langugae parameter add
      formData.append("language", language);
      
      // HERO SECTION
      formData.append("heroSection", JSON.stringify({ ...heroData, backgroundImage: "" }));
      if (heroFile) formData.append("heroBackgroundImage", heroFile);

      //  FEATURES SECTION
      formData.append("featuresSection", JSON.stringify(featuresList.map(feature => ({
        subtitle: feature.subtitle || "", title: feature.title || "", icon: ""
      }))));
      
      featureIconFiles.forEach((file, index) => {
        if (file) formData.append(`featureIcon_${index}`, file);
      });

      // ABOUT SECTION
      formData.append("aboutSection", JSON.stringify({
        ...aboutData,
        images: [],
        services: (aboutData.services || []).map(service => ({
          serviceTitle: service.serviceTitle || "", serviceIcon: ""
        }))
      }));
      
      serviceIconFiles.forEach((file, index) => {
        if (file) formData.append(`serviceIcon_${index}`, file);
      });
      
      if (aboutFiles.length > 0) {
        aboutFiles.forEach(file => formData.append("aboutImages", file));
      }
      
      // appointment section
      formData.append("appointmentSection", JSON.stringify(appointmentData));
      if (appointmentFile) formData.append("appointmentImage", appointmentFile);
      
      // why choose us section
      formData.append("whyChooseUsSection", JSON.stringify({
        title: whyChooseData.title || "",
        subtitle: whyChooseData.subtitle || "",
        description: whyChooseData.description || "",
        services: whyChooseData.services.map(service => ({
          serviceTitle: service.serviceTitle || "",
          serviceDescription: service.serviceDescription || "",
          serviceIcon: ""
        })),
        image: ""
      }));
      
      if (whyChooseFile) formData.append("whyChooseUsImage", whyChooseFile);
      whyChooseServiceIconFiles.forEach((file, index) => {
        if (file) formData.append(`whyChooseServiceIcon_${index}`, file);
      });
      
      // download app section
      formData.append("downloadAppSection", JSON.stringify({ ...downloadAppData, image: "" }));
      if (downloadAppFile) formData.append("downloadAppImage", downloadAppFile);
      
      // appointment process
      formData.append("appointmentProcess", JSON.stringify(
        appointmentProcessData.map(process => ({ title: process.title || "", icon: "" }))
      ));
      
      appointmentProcessIconFiles.forEach((file, index) => {
        if (file) formData.append(`appointmentProcessIcon_${index}`, file);
      });

      // language parameter add
      await axios.post(`${API}?language=${language}`, formData, { 
        headers: { "Content-Type": "multipart/form-data" } 
      });
      
      showSuccessMessage(`Homepage created successfully for ${language} language!`);
      setHomepageExists(true);
    } catch (err) {
      console.error("Error creating homepage:", err);
      showErrorMessage("Error creating homepage: " + (err.response?.data?.error || err.message));
    }
  };

  // homepage update handelar
  const handleUpdateHomepage = async () => {
    try {
      // টেক্সট কন্টেন্ট আপডেট
      const updatePromises = [
        axios.put(`${API}/seo`, {
          language,
          metaTitle: seoData.metaTitle || "",
          metaDescription: seoData.metaDescription || "",
        }),

        axios.put(`${API}/heroSection`, { language, translations: heroData }),
        
        axios.put(`${API}/featuresSection`, { 
          language, 
          translations: featuresList.map(f => ({
            subtitle: f.subtitle || "", 
            title: f.title || "", 
            icon: f.icon !== "icon-selected" ? f.icon : ""
          }))
        }),
        
        axios.put(`${API}/aboutSection`, {
          language,
          translations: {
            ...aboutData,
            services: (aboutData.services || []).map(s => ({
              serviceTitle: s.serviceTitle || "",
              icon: s.icon !== "icon-selected" ? s.icon : ""
            }))
          }
        }),
        
        axios.put(`${API}/appointmentSection`, { language, translations: appointmentData }),
        
        axios.put(`${API}/whyChooseUsSection`, {
          language,
          translations: {
            ...whyChooseData,
            services: whyChooseData.services.map(s => ({
              serviceTitle: s.serviceTitle || "",
              serviceDescription: s.serviceDescription || "",
              serviceIcon: s.serviceIcon !== "icon-selected" ? s.serviceIcon : ""
            }))
          }
        }),
        
        axios.put(`${API}/downloadAppSection`, { language, translations: downloadAppData }),
        
        axios.put(`${API}/appointmentProcess`, {
          language,
          translations: appointmentProcessData.map(p => ({
            title: p.title || "",
            icon: p.icon !== "icon-selected" ? p.icon : ""
          }))
        })
      ];
      
      await Promise.all(updatePromises);
      
      // ইমেজ আপডেট
      const uploadImages = async () => {
        const uploadFile = async (file, endpoint, fieldName) => {
          if (!file) return;
          const formData = new FormData();
          formData.append(fieldName, file);
          formData.append("language", language);
          return axios.post(`${API}/${endpoint}?language=${language}`, formData, { 
            headers: { "Content-Type": "multipart/form-data" } 
          });
        };
        
        const uploadFiles = async (files, endpoint, fieldName) => {
          if (!files || !files.some(f => f !== null)) return;
          const formData = new FormData();
          
          formData.append("language", language);
          files.forEach((file, index) => {
            if (file) formData.append(`${fieldName}_${index}`, file);
          });
          
          return axios.post(`${API}/${endpoint}?language=${language}`, formData, { 
            headers: { "Content-Type": "multipart/form-data" } 
          });
        };
        
        // const uploadMultiple = async (files, endpoint, fieldName) => {
        //   if (!files || !files.length) return;
        //   const formData = new FormData();
        //   formData.append("language", language);
        //   files.forEach(file => formData.append(fieldName, file));
        //   return axios.post(`${API}/${endpoint}?language=${language}`, formData, { 
        //     headers: { "Content-Type": "multipart/form-data" } 
        //   });
        // };

        const uploadMultiple = async (files, endpoint, fieldName) => {
            if (!files || !files.length) return;
            const formData = new FormData();
            formData.append("language", language);
            files.forEach(file => formData.append(fieldName, file));
            return axios.post(`${API}/${endpoint}?language=${language}`, formData, {
              headers: { "Content-Type": "multipart/form-data" }
            });
          };
        
        
        const imagePromises = [
          uploadFiles(featureIconFiles, "uploadFeatureIcons", "featureIcon"),
          uploadFiles(serviceIconFiles, "uploadServiceIcons", "serviceIcon"),
          uploadFiles(whyChooseServiceIconFiles, "uploadWhyChooseServiceIcons", "whyChooseServiceIcon"),
          uploadFiles(appointmentProcessIconFiles, "uploadAppointmentProcessIcons", "appointmentProcessIcon"),
          uploadFile(heroFile, "uploadHeroImage", "heroBackgroundImage"),
          uploadFile(appointmentFile, "uploadAppointmentImage", "appointmentImage"),
          uploadFile(whyChooseFile, "uploadWhyChooseImage", "whyChooseUsImage"),
          uploadFile(downloadAppFile, "uploadDownloadAppImage", "downloadAppImage"),
          uploadMultiple(aboutFiles, "uploadAboutImages", "aboutImages") // This line is important
        ].filter(Boolean);
        
        if (imagePromises.length > 0) {
          await Promise.all(imagePromises);
        }
      };
      
      await uploadImages();
      
      showSuccessMessage(`Homepage updated successfully for ${language} language!`);

      setTimeout(() => {
        // Reset file selections
        setHeroFile(null);
        setAppointmentFile(null);
        setWhyChooseFile(null);
        setDownloadAppFile(null);
        setAboutFiles([]);
        setFeatureIconFiles(new Array(featuresList.length).fill(null));
        setServiceIconFiles(new Array((aboutData.services || []).length).fill(null));
        setWhyChooseServiceIconFiles(new Array(whyChooseData.services.length).fill(null));
        setAppointmentProcessIconFiles(new Array(appointmentProcessData.length).fill(null));

        // Refresh data
        fetchHomepage();
      }, 3000);
    } catch (err) {
      console.error("Error updating homepage:", err);
      showErrorMessage("Error updating homepage: " + (err.response?.data?.error || err.message));
    }
  };
  
  //form submit handelar
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
  
    try {
      if (!homepageExists) {
        await handleCreateHomepage();
      } else {
        await handleUpdateHomepage();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

      // ----------------------------------------------------------------
  //  B) Language Switch (English <-> Bangla)
  // ----------------------------------------------------------------
  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'bn', label: 'Bangla' }
  ];

  const handleLanguageChange = (selectedOption) => {
    setLanguage(selectedOption.value);
  };

  // return loading state
  if (isLoading) {
    return (
      <div className="p-6 flex justify-center items-center min-h-screen">
        <div className="text-center">
          <FaSpinner className="animate-spin h-10 w-10 mx-auto text-blue-500" />
          <p className="mt-4 text-gray-600">Loading homepage data...</p>
        </div>
      </div>
    );
  }
  
  // item component render function
  const renderFeature = (feature, index) => (
    <div key={index} className="mb-4 border p-4 rounded-lg bg-white relative">
      <h3 className="font-medium mb-2">Feature #{index + 1}</h3>
      <FormInput
        label="Subtitle" id={`featureSubtitle_${index}`}
        value={feature.subtitle} placeholder="Enter subtitle"
        onChange={(e) => handleFeatureChange(index, "subtitle", e.target.value)}
      />
      <FormInput
        label="Title" id={`featureTitle_${index}`}
        value={feature.title} placeholder="Enter title"
        onChange={(e) => handleFeatureChange(index, "title", e.target.value)}
      />
      <IconPreview file={featureIconFiles[index]} icon={feature.icon} />
      <IconUpload
        id={`featureIcon_${index}`}
        onChange={(e) => handleFeatureIconUpload(index, e.target.files[0])}
        filename={featureIconFiles[index]?.name}
      />
      
      {featuresList.length > 1 && (
        <button
          type="button"
          onClick={() => removeFeature(index)}
          className="absolute top-2 right-2 text-red-600 hover:text-red-800"
        >
          <FaTrash size={12}/>
        </button>
      )}
    </div>
  );

  const renderService = (service, index) => (
    <div key={index} className="mb-4 border p-4 rounded-lg bg-white relative">
      <h3 className="font-medium mb-2">Service #{index + 1}</h3>
      <FormInput
        label="Service Title" id={`serviceTitle_${index}`}
        value={service.serviceTitle} placeholder="Enter service title"
        onChange={(e) => handleServiceChange(index, "serviceTitle", e.target.value)}
      />
      <IconPreview file={serviceIconFiles[index]} icon={service.icon} />
      <IconUpload
        id={`serviceIcon_${index}`}
        onChange={(e) => handleServiceIconUpload(index, e.target.files[0])}
        filename={serviceIconFiles[index]?.name}
      />
      
      {(aboutData.services || []).length > 1 && (
        <button
          type="button"
          onClick={() => removeService(index)}
          className="absolute top-2 right-2 text-red-600 hover:text-red-800"
        >
          <FaTrash size={12}/>
        </button>
      )}
    </div>
  );

  const renderWhyChooseService = (service, index) => (
    <div key={index} className="mb-4 border p-4 rounded-lg bg-white relative">
      <h3 className="font-medium mb-2">Service #{index + 1}</h3>
      <FormInput
        label="Service Title" id={`whyChooseTitle_${index}`}
        value={service.serviceTitle} placeholder="Enter service title"
        onChange={(e) => handleWhyChooseChange(index, "serviceTitle", e.target.value)}
      />
      <FormTextarea
        label="Service Description" id={`whyChooseDesc_${index}`}
        value={service.serviceDescription}
        onChange={(e) => handleWhyChooseChange(index, "serviceDescription", e.target.value)}
      />
      <IconPreview file={whyChooseServiceIconFiles[index]} icon={service.serviceIcon} />
      <IconUpload
        id={`whyChooseServiceIcon_${index}`}
        onChange={(e) => handleWhyChooseServiceIconUpload(index, e.target.files[0])}
        filename={whyChooseServiceIconFiles[index]?.name}
      />
      
      {whyChooseData.services.length > 1 && (
        <button
          type="button"
          onClick={() => removeChooseService(index)}
          className="absolute top-2 right-2 text-red-600 hover:text-red-800"
        >
          <FaTrash size={12}/>
        </button>
      )}
    </div>
  );

  const renderAppointmentProcess = (process, index) => (
    <div key={index} className="mb-4 border p-4 rounded-lg bg-white relative">
      <h3 className="font-medium mb-2">Step #{index + 1}</h3>
      <FormInput
        label="Title" id={`processTitle_${index}`}
        value={process.title} placeholder="Enter title"
        onChange={(e) => handleAppointmentProcessChange(index, "title", e.target.value)}
      />
      <IconPreview file={appointmentProcessIconFiles[index]} icon={process.icon} />
      <IconUpload
        id={`appointmentProcessIcon_${index}`}
        onChange={(e) => handleAppointmentProcessIconUpload(index, e.target.files[0])}
        filename={appointmentProcessIconFiles[index]?.name}
      />

      {appointmentProcessData.length > 1 && (
        <button
          type="button"
          onClick={() => removeAppointmentProcess(index)}
          className="absolute top-2 right-2 text-red-600 hover:text-red-800"
        >
          <FaTrash size={12}/>
        </button>
      )}
    </div>
  );
  
  // ─────────────────────────────────────────────────────────────────────────────
  // Render form
  // ─────────────────────────────────────────────────────────────────────────────
  const breadcrumbs = [
    { label: 'Dashboard', url: '/' },
    { label: 'Manage Sections'},
    { label: 'HomePage Section' }
  ];

  return (
    <div>
      <ToastContainer position="top-right" autoClose={3000} />
      <PageHeading title="Create / Update Homepage" breadcrumbs={breadcrumbs} />
    
      {/* language select and copy button */}
      <div className="mb-6 flex flex-col md:flex-row gap-5 items-start  md:items-center justify-between">
        <DynamicSelect
          options={languageOptions}  // Pass options for languages
          value={languageOptions.find(option => option.value === language)}
          onChange={handleLanguageChange}
          isSearchable={false}
          placeholder="Select a language"
        />
      </div>

      <form onSubmit={handleSubmit} encType="multipart/form-data" className="space-y-8">
        {/* SEO settings section */}
        <div className="bg-white shadow-sm rounded-lg mt-5">
          <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              SEO Settings (Meta Title &amp; Description)
            </h2>
          </div>
          <div className="p-5">
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <label className="label" htmlFor="seoMetaTitle">Meta Title</label>
                <span className={`text-xs ${(seoData.metaTitle || "").length > 60 ? "text-red-600 font-semibold" : "text-gray-400"}`}>
                  {(seoData.metaTitle || "").length}/60
                </span>
              </div>
              <input
                className="input-field"
                type="text"
                id="seoMetaTitle"
                placeholder="Enter meta title (recommended within 60 characters)"
                value={seoData.metaTitle || ""}
                onChange={(e) => setSeoData({ ...seoData, metaTitle: e.target.value })}
              />
            </div>
            <div className="mb-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="seoMetaDescription">Meta Description</label>
                <span className={`text-xs ${(seoData.metaDescription || "").length > 160 ? "text-red-600 font-semibold" : "text-gray-400"}`}>
                  {(seoData.metaDescription || "").length}/160
                </span>
              </div>
              <textarea
                className="input-field"
                rows={3}
                id="seoMetaDescription"
                placeholder="Enter meta description (recommended within 160 characters)"
                value={seoData.metaDescription || ""}
                onChange={(e) => setSeoData({ ...seoData, metaDescription: e.target.value })}
              />
            </div>
            <p className="text-xs text-gray-400">
              Saved per language. If left empty, the website will use its default fallback title and description.
            </p>
          </div>
        </div>

        {/* hero section */}
        <div className="bg-white shadow-sm rounded-lg mt-5">
          <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                Hero Section
                </h2>
              </div>
         <div className="p-5">
          <FormInput
              label="Prefix" id="prefix"
              value={heroData.prefix}
              onChange={(e) => setHeroData({ ...heroData, prefix: e.target.value })}
            />
            <FormInput
              label="Title" id="title"
              value={heroData.title}
              onChange={(e) => setHeroData({ ...heroData, title: e.target.value })}
            />
          <ImageUpload
            id="heroImage"
            label="Background Image"
            file={heroFile}
            setFile={setHeroFile}
            currentImage={heroData.backgroundImage}
          />
           </div>
        </div>
        
        {/*feature section */}
        <div className="bg-white shadow-sm rounded-lg mt-5">
        <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                Features Section
                </h2>
                <p className="text-sm text-gray-500">Total Features: {featuresList.length}</p>
              </div>
          <div className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5 mt-5">
           {featuresList.map(renderFeature)}
          </div>
          <button
            type="button"
            onClick={addFeature}
            className="mt-2 text-blue-500 text-sm hover:text-blue-700 flex items-center gap-1"
          >
            <FaPlus size={10} /> Add Feature
          </button>
          </div>
        </div>
        
        {/* About Section */}
        <div className="bg-white shadow-sm rounded-lg mt-5">
          <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
            About Section
            </h2>
            </div>
         <div className="p-5">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
          <div>
          <FormInput
              label="Title" id="aboutTitle"
              value={aboutData.title}
              onChange={(e) => setAboutData({ ...aboutData, title: e.target.value })}
            />
            <FormInput
              label="Subtitle" id="aboutSubtitle"
              value={aboutData.subtitle}
              onChange={(e) => setAboutData({ ...aboutData, subtitle: e.target.value })}
            />
            <FormInput
              label="Experience" id="experience"
              value={aboutData.experience}
              onChange={(e) => setAboutData({ ...aboutData, experience: e.target.value })}
            />
          </div>
          {/* About Images Section */}
          {/* <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="images">
              Select About Images (up to 4):
            </label>
            <input
              type="file" id="images" multiple
              onChange={(e) => handleFileChange(e, setAboutFiles, 4)}
              className="w-full p-2 border rounded-md bg-white focus:ring-2 focus:ring-blue-500"
            />
            
            {aboutFiles.length > 0 && (
              <div className="mt-2 text-sm text-gray-500">
                Selected files: {aboutFiles.map(file => file.name).join(', ')}
              </div>
            )}

            {aboutFiles.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">New Images Preview:</p>
                <div className="flex flex-wrap gap-4">
                  {aboutFiles.map((file, index) => (
                    <div key={index} className="w-32 h-32 overflow-hidden rounded-md border">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`preview-${index}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {!aboutFiles.length && aboutData.images && aboutData.images.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Current Images:</p>
                <div className="flex flex-wrap gap-4">
                  {aboutData.images.map((image, index) => (
                    <div key={index} className="w-32 h-32 overflow-hidden rounded-md border">
                      <img
                        src={getImageUrl(image)}
                        alt={`about-image-${index}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div> */}
          <div className="mb-4">
  <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="images">
    Select About Images (up to 3):
  </label>
  <input
    type="file"
    id="images"
    multiple
    onChange={(e) => handleFileChange(e, setAboutFiles, 3)}
    className="w-full p-2 border rounded-md bg-white focus:ring-2 focus:ring-blue-500"
    accept="image/jpeg,image/png,image/gif,image/webp"
  />
  
  {/* Preview for newly selected images */}
  {aboutFiles.length > 0 && (
    <div className="mt-4">
      <p className="text-sm font-medium text-gray-700 mb-2">New Images Preview (Drag to reorder):</p>
      <div
        className="flex flex-wrap gap-4"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const draggedIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);
          const targetIndex = parseInt(e.target.closest("[data-index]")?.dataset.index, 10);
          if (!isNaN(draggedIndex) && !isNaN(targetIndex) && draggedIndex !== targetIndex) {
            setAboutFiles((prev) => {
              const newFiles = [...prev];
              const [draggedFile] = newFiles.splice(draggedIndex, 1);
              newFiles.splice(targetIndex, 0, draggedFile);
              return newFiles;
            });
          }
        }}
      >
        {aboutFiles.map((file, index) => (
          <div
            key={index}
            data-index={index}
            className="w-32 h-32 relative overflow-hidden rounded-md border"
            draggable
            onDragStart={(e) => e.dataTransfer.setData("text/plain", index)}
          >
            <img
              src={URL.createObjectURL(file)}
              alt={`preview-${index}`}
              className="w-full h-full object-cover"
              onError={() => toast.error(`Failed to load image: ${file.name}`)}
            />
            <button
              type="button"
              onClick={() => setAboutFiles((prev) => prev.filter((_, i) => i !== index))}
              className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 hover:bg-red-800 transition-all duration-200"
              title="Delete Image"
            >
              <FaTrash size={10} />
            </button>
          </div>
        ))}
      </div>
    </div>
  )}
  
  {/* Preview for existing images */}
  {!aboutFiles.length && aboutData.images && aboutData.images.length > 0 && (
    <div className="mt-4">
      <p className="text-sm font-medium text-gray-700 mb-2">Current Images (Drag to reorder):</p>
      <div
        className="flex flex-wrap gap-4"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const draggedIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);
          const targetIndex = parseInt(e.target.closest("[data-index]")?.dataset.index, 10);
          if (!isNaN(draggedIndex) && !isNaN(targetIndex) && draggedIndex !== targetIndex) {
            setAboutData((prev) => {
              const newImages = [...prev.images];
              const [draggedImage] = newImages.splice(draggedIndex, 1);
              newImages.splice(targetIndex, 0, draggedImage);
              return { ...prev, images: newImages };
            });
          }
        }}
      >
        {aboutData.images.map((image, index) => (
          <div
            key={index}
            data-index={index}
            className="w-32 h-32 relative overflow-hidden rounded-md border"
            draggable
            onDragStart={(e) => e.dataTransfer.setData("text/plain", index)}
          >
            <img
              src={getImageUrl(image)}
              alt={`about-image-${index}`}
              className="w-full h-full object-cover"
              onError={() => toast.error(`Failed to load image: ${image}`)}
            />
            <button
              type="button"
              onClick={() => setAboutData((prev) => ({
                ...prev,
                images: prev.images.filter((_, i) => i !== index),
              }))}
              className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 hover:bg-red-800 transition-all duration-200"
              title="Delete Image"
            >
              <FaTrash size={10} />
            </button>
          </div>
        ))}
      </div>
    </div>
  )}
          </div>
          </div>
          <FormTextarea
              label="Description" id="aboutDesc"
              value={aboutData.description}
              onChange={(e) => setAboutData({ ...aboutData, description: e.target.value })}
            />
          {/* Service */}
          <div className="mt-8">
             <div className="flex items-center justify-between">
                <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                Services
                </h2>
                <p className="text-sm text-gray-500">Total Features: {aboutData.services?.length || 0}</p>
              </div>
           <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5 mt-5">
             {(aboutData.services || []).map(renderService)}
           </div>
            <button
              type="button"
              onClick={addService}
              className="mt-2 text-blue-500 text-sm hover:text-blue-700 flex items-center gap-1"
            >
              <FaPlus size={10} /> Add Service
            </button>
          </div>
         </div>
        </div>
        
        {/* Appoinment Section */}
        <div className="bg-white shadow-sm rounded-lg mt-5">
        <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
            Appointment Section
            </h2>
            </div>
          <div className="p-5">
            <ImageUpload
              id="appointmentImage"
              label="Appointment Image"
              file={appointmentFile}
              setFile={setAppointmentFile}
              imgClassName="w-32" 
              currentImage={appointmentData.image}
            />
          </div>
        </div>
        
        {/* why choose us section */}
        <div className="bg-white shadow-sm rounded-lg mt-5">
        <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
            Why Choose Us Section
            </h2>
            </div>
          <div className="p-5">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
            <div>
              <FormInput
                label="Title" id="whyChooseTitle"
                value={whyChooseData.title}
                onChange={(e) => setWhyChooseData({ ...whyChooseData, title: e.target.value })}
              />
              <FormInput
                label="Subtitle" id="whyChooseSubtitle"
                value={whyChooseData.subtitle}
                onChange={(e) => setWhyChooseData({ ...whyChooseData, subtitle: e.target.value })}
              />
              <FormTextarea
                label="Description" id="whyChooseDesc"
                value={whyChooseData.description}
                onChange={(e) => setWhyChooseData({ ...whyChooseData, description: e.target.value })}
              />
            </div>
          <ImageUpload
            id="whyChooseImage"
            label="Why Choose Us Image"
            file={whyChooseFile}
            setFile={setWhyChooseFile}
            imgClassName="w-[150px]" 
            currentImage={whyChooseData.image}
          />
            </div>
          
          {/* Service */}
          <div className="mt-4">
            <div className="flex items-center justify-between">
                <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                Why Choose Us Services
                </h2>
                <p className="text-sm text-gray-500">Total Services: {whyChooseData.services.length}</p>
              </div>
           <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5 mt-5">
              {whyChooseData.services.map(renderWhyChooseService)}
           </div>
            <button
              type="button"
              onClick={addChooseService}
              className="mt-2 text-blue-500 text-sm hover:text-blue-700 flex items-center gap-1"
            >
              <FaPlus size={10}/> Add Service
            </button>
          </div>
          </div>
        </div>
        
        {/* download app section */}
        <div className="bg-white shadow-sm rounded-lg mt-5">
            <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
            Download App Section
            </h2>
            </div>
         <div className="p-5">
         <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
            <div>
            <FormInput
                label="Title" id="downloadTitle"
                value={downloadAppData.title}
                onChange={(e) => setDownloadAppData({ ...downloadAppData, title: e.target.value })}
              />
              <FormInput
                label="Subtitle" id="downloadSubtitle"
                value={downloadAppData.subtitle}
                onChange={(e) => setDownloadAppData({ ...downloadAppData, subtitle: e.target.value })}
              />
              <FormTextarea
                label="Description" id="downloadDesc"
                value={downloadAppData.description}
                onChange={(e) => setDownloadAppData({ ...downloadAppData, description: e.target.value })}
              />
            </div>
              <ImageUpload
                id="downloadAppImage"
                label="App Image"
                file={downloadAppFile}
                setFile={setDownloadAppFile}
                imgClassName="w-48" 
                currentImage={downloadAppData.image}
              />
         </div>
        </div>
        </div>
        
        {/* Appoinment Process Section */}
        <div className="bg-white shadow-sm rounded-lg mt-5">
        <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
            Appointment Process Section
            </h2>
            <div className="mb-2">
            <p className="text-sm text-gray-500">Total Steps: {appointmentProcessData.length}</p>
          </div>
            </div>
       <div className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 mt-5">
            {appointmentProcessData.map(renderAppointmentProcess)}
          </div>
          <button
            type="button"
            onClick={addAppointmentProcess}
            className="mt-4 text-blue-500 text-sm hover:text-blue-700 flex items-center gap-1"
          >
            <FaPlus size={10}/> Add Appointment Process
          </button>
       </div>
        </div>
        {/* SEO Configuration */}
        {/* <div className="bg-white shadow-sm rounded-lg mt-5">
             <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                  SEO Configuration
                </h2>
              </div>
            <div className="p-5 grid grid-cols-2 gap-6">
           
                <div>
                  <label className="label">Meta Title</label>
                  <input
                    type="text"
                    name="metaTitle"
                    className="input-field"
                    placeholder="Enter Meta Title"
                  />
                </div>

               
                <div>
                  <label className="label">Meta Description</label>
                  <input
                    type="text"
                    name="metaDescription"
                    className="input-field"
                    placeholder="Enter Meta Description"
                  />
                </div>
            </div>
         </div> */}
        
        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
        >
          {isSubmitting ? (
            <>
              <FaSpinner className="animate-spin mr-2" />
              {homepageExists ? "Updating..." : "Creating..."}
            </>
          ) : (
            <>{homepageExists ? `Update Content` : `Create Content`}</>
          )}
        </button>
      </form>
    </div>
  );
};

export default HomepageForm;